import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useProjectStore } from '../lib/store';

const GanttView = ({ projectId, onTaskClick }) => {
  const { fetchGanttData } = useProjectStore();
  const [ganttData, setGanttData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewStart, setViewStart] = useState(new Date());
  const [daysToShow, setDaysToShow] = useState(28); // 4 weeks

  useEffect(() => {
    if (projectId) {
      loadGanttData();
    }
  }, [projectId]);

  const loadGanttData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchGanttData(projectId);
      setGanttData(data);
      
      // Find earliest start date
      if (data.tasks?.length > 0) {
        const dates = data.tasks.map(t => new Date(t.start));
        const earliestDate = new Date(Math.min(...dates));
        // Start 2 days before earliest task
        earliestDate.setDate(earliestDate.getDate() - 2);
        setViewStart(earliestDate);
      }
    } catch (error) {
      console.error('Failed to load Gantt data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const getDaysFromStart = (date) => {
    return getDaysBetween(viewStart, date);
  };

  const formatDateHeader = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const generateDates = () => {
    const dates = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(viewStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const shiftView = (days) => {
    const newStart = new Date(viewStart);
    newStart.setDate(newStart.getDate() + days);
    setViewStart(newStart);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-destructive/80';
      case 'medium': return 'bg-chart-4/80';
      default: return 'bg-chart-3/80';
    }
  };

  const getStatusOpacity = (status) => {
    switch (status) {
      case 'done': return 'opacity-70';
      case 'in_progress': return 'opacity-90';
      default: return 'opacity-100';
    }
  };

  const dates = generateDates();
  const dayWidth = 40; // pixels per day

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!ganttData || ganttData.tasks?.length === 0) {
    return (
      <Card className="glass">
        <CardContent className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
          <Calendar className="h-12 w-12 mb-4 opacity-50" />
          <p>No tasks with dates to display</p>
          <p className="text-sm mt-1">Add due dates to tasks to see them on the Gantt chart</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Gantt Chart
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => shiftView(-7)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewStart(new Date())}>
              Today
            </Button>
            <Button variant="ghost" size="icon" onClick={() => shiftView(7)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full" orientation="horizontal">
          <div className="min-w-max">
            {/* Header with dates */}
            <div className="flex border-b border-white/10 pb-2 mb-2">
              <div className="w-[200px] flex-shrink-0 font-medium text-sm">Task</div>
              <div className="flex">
                {dates.map((date, i) => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  return (
                    <div
                      key={i}
                      className={`text-xs text-center ${isWeekend ? 'text-muted-foreground' : ''} ${isToday ? 'font-bold text-primary' : ''}`}
                      style={{ width: dayWidth }}
                    >
                      {formatDateHeader(date)}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tasks */}
            <div className="space-y-1">
              {ganttData.tasks.map((task, index) => {
                const startOffset = getDaysFromStart(task.start);
                const duration = getDaysBetween(task.start, task.end);
                const barLeft = Math.max(0, startOffset) * dayWidth;
                const barWidth = Math.min(duration, daysToShow - Math.max(0, startOffset)) * dayWidth;
                
                // Skip if task is completely outside view
                if (startOffset + duration < 0 || startOffset > daysToShow) {
                  return null;
                }

                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center h-10"
                  >
                    {/* Task name */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="w-[200px] flex-shrink-0 text-sm truncate pr-2 flex items-center gap-1 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => onTaskClick?.(task.id)}
                          >
                            {task.is_business_critical && (
                              <AlertTriangle className="h-3 w-3 text-destructive flex-shrink-0" />
                            )}
                            <span className="truncate">{task.name}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{task.name}</p>
                          <p className="text-xs text-muted-foreground">{task.start} → {task.end}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Timeline bar container */}
                    <div className="relative flex-1 h-full" style={{ minWidth: daysToShow * dayWidth }}>
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {dates.map((date, i) => {
                          const isToday = date.toDateString() === new Date().toDateString();
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          return (
                            <div
                              key={i}
                              className={`border-l h-full ${isToday ? 'border-primary/50' : 'border-white/5'} ${isWeekend ? 'bg-white/[0.02]' : ''}`}
                              style={{ width: dayWidth }}
                            />
                          );
                        })}
                      </div>

                      {/* Task bar */}
                      {barWidth > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`absolute top-1 h-8 rounded cursor-pointer transition-all hover:brightness-110 ${getPriorityColor(task.priority)} ${getStatusOpacity(task.status)}`}
                                style={{
                                  left: barLeft,
                                  width: Math.max(barWidth, 20),
                                }}
                                onClick={() => onTaskClick?.(task.id)}
                              >
                                {/* Progress indicator */}
                                <div
                                  className="h-full rounded bg-white/20"
                                  style={{ width: `${task.progress}%` }}
                                />
                                {/* Task name inside bar (if wide enough) */}
                                {barWidth > 60 && (
                                  <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-white truncate">
                                    {task.name}
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="space-y-1">
                                <p className="font-medium">{task.name}</p>
                                <p className="text-xs">
                                  {task.start} → {task.end}
                                </p>
                                <p className="text-xs">
                                  Progress: {task.progress}% | Status: {task.status.replace('_', ' ')}
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 text-xs">
              <div className="flex items-center gap-1">
                <div className="h-3 w-3 rounded bg-destructive/80" />
                <span>High Priority</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-3 w-3 rounded bg-chart-4/80" />
                <span>Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-3 w-3 rounded bg-chart-3/80" />
                <span>Low</span>
              </div>
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-destructive" />
                <span>Critical</span>
              </div>
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default GanttView;
